import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { emailIngestionAPI } from '../services/api';
import { toast } from 'sonner';
import {
  LogOut, Menu, X, ChevronDown, RefreshCw, Loader2, Users, Mail, KeyRound,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  navigation, partWorkflow, salesNav, wireNav, productionNav,
  engineeringNav, masterData, adminNav,
} from './layout/navConfig';

const LOGO_URL = "/logo-color.jpg";

const NavLink = ({ item, active, onNavigate }) => (
  <Link
    to={item.href}
    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'bg-white/20 text-white'
        : 'text-white/70 hover:bg-white/10 hover:text-white'
    }`}
    onClick={onNavigate}
  >
    <item.icon className="h-4 w-4" />
    {item.name}
  </Link>
);

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  
  // Email sync status state
  const [syncStatus, setSyncStatus] = React.useState({ has_accounts: false, last_sync_at: null, syncing: false });
  const [syncLoading, setSyncLoading] = React.useState(false);

  // Load sync status on mount and periodically
  React.useEffect(() => {
    const fetchSyncStatus = async () => {
      try {
        const res = await emailIngestionAPI.getSyncStatus();
        setSyncStatus(res.data);
      } catch (err) {
      }
    };
    
    fetchSyncStatus();
    const interval = setInterval(fetchSyncStatus, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Manual sync trigger
  const handleManualSync = async () => {
    setSyncLoading(true);
    try {
      await emailIngestionAPI.sync();
      toast.success('Email sync started');
      // Refresh status after a short delay
      setTimeout(async () => {
        const res = await emailIngestionAPI.getSyncStatus();
        setSyncStatus(res.data);
        setSyncLoading(false);
      }, 2000);
    } catch (err) {
      toast.error('Failed to start email sync');
      setSyncLoading(false);
    }
  };

  // Format relative time for last sync
  const formatLastSync = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => location.pathname === href || location.pathname.startsWith(href + '/');

  const renderNavLink = (item) => (
    <NavLink
      key={item.href}
      item={item}
      active={isActive(item.href)}
      onNavigate={() => setSidebarOpen(false)}
    />
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Corporate Royal Blue */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 sidebar-corporate transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo & Company Name */}
          <div className="p-4 border-b border-white/10">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="bg-white rounded-lg p-1.5">
                <img src={LOGO_URL} alt="Sturdfix" className="h-10 w-auto" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-white font-heading font-bold text-lg leading-tight truncate">
                  STURDFIX
                </h1>
                <p className="text-white/60 text-xs leading-tight truncate">
                  Singhania International Ltd.
                </p>
              </div>
            </Link>
            <button
              className="lg:hidden absolute top-4 right-4 text-white/70 hover:text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin scrollbar-light">
            <>
              {/* Main nav */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                  Main
                </p>
                {navigation.map(renderNavLink)}
              </div>

              {/* Part-First Workflow (Central) */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                  Part Workflow
                </p>
                {partWorkflow.map(renderNavLink)}
              </div>

              {/* Sales & Quotations */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                  Sales
                </p>
                {salesNav.map(renderNavLink)}
              </div>

              {/* Wire — separate from fastener flow */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                  Wire
                </p>
                {wireNav.map(renderNavLink)}
              </div>

              {/* Production Planning — disabled (Feb 2026, marketing-led pivot) */}
              {productionNav.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                    Production
                  </p>
                  {productionNav.map(renderNavLink)}
                </div>
              )}

              {/* Master Data */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                  Engineering Tools
                </p>
                  {engineeringNav.map(renderNavLink)}
                </div>

                {/* Master Data */}
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                    Master Data
                  </p>
                  {masterData.filter(item => !item.hidden).map(renderNavLink)}
                </div>

                {/* Admin */}
                {(user?.role === 'admin' || user?.role === 'manager') && (
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-2 px-3">
                      Administration
                    </p>
                    {adminNav
                      .filter((item) => user?.role === 'admin' || item.name !== 'Users')
                      .map((item) => (
                        <NavLink key={item.name} item={item} />
                      ))}
                  </div>
                )}
              </>
          </nav>

          {/* User menu */}
          <div className="p-4 border-t border-white/10">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full px-3 py-2 rounded-md hover:bg-white/10 transition-colors">
                  <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="text-sm font-semibold text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                    <p className="text-xs text-white/60 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-white/60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <Users className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => navigate('/change-password')}
                  data-testid="user-menu-change-password"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar - Corporate Header */}
        <header className="sticky top-0 z-30 glass">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              className="lg:hidden text-muted-foreground hover:text-foreground"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </button>
            
            {/* Mobile logo */}
            <div className="flex-1 lg:hidden text-center">
              <span className="font-heading font-bold text-primary">STURDFIX</span>
            </div>
            
            {/* Desktop header content */}
            <div className="hidden lg:flex items-center gap-4 ml-auto">
              {/* Email Sync Status Indicator */}
              {syncStatus.has_accounts && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground border-r pr-4 mr-2">
                  <Mail className="h-3.5 w-3.5" />
                  <span>
                    Last sync: {syncStatus.syncing ? 'Syncing...' : formatLastSync(syncStatus.last_sync_at)}
                  </span>
                  <button
                    onClick={handleManualSync}
                    disabled={syncLoading || syncStatus.syncing}
                    className="p-1 rounded hover:bg-muted transition-colors disabled:opacity-50"
                    title="Sync Now"
                    data-testid="sync-now-btn"
                  >
                    {syncLoading || syncStatus.syncing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                  </button>
                </div>
              )}
              <span className="text-sm text-muted-foreground">
                Singhania International Limited
              </span>
            </div>
            
            {/* Mobile sync button (only show if accounts configured) */}
            {syncStatus.has_accounts && (
              <button
                onClick={handleManualSync}
                disabled={syncLoading || syncStatus.syncing}
                className="lg:hidden p-2 text-muted-foreground hover:text-foreground disabled:opacity-50"
                title="Sync Emails"
                data-testid="mobile-sync-btn"
              >
                {syncLoading || syncStatus.syncing ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <RefreshCw className="h-5 w-5" />
                )}
              </button>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
